import CustomerPicker from '../CustomerPicker';
import DatePicker from '../DatePicker';
import InvoiceLineItemsEditForm from './InvoiceLineItemsEditForm';
import {Box, FormField, Input, Textarea} from '@sproutsocial/racine';
import {DateTime} from 'luxon';
import {SyntheticEvent, useCallback, useEffect, useRef} from 'react';
import {Invoice, InvoiceLineItem} from 'shared/src/models';

interface InvoiceEditFormProps {
	invoice: Invoice;
	onInvoiceUpdated: (updatedInvoice: Invoice) => void;
}

const InvoiceEditForm = ({invoice, onInvoiceUpdated}: InvoiceEditFormProps) => {
	// Use refs to avoid creating new callbacks on every keystroke
	const invoiceRef = useRef<Invoice>(invoice);

	useEffect(() => {
		invoiceRef.current = invoice;
	}, [invoiceRef, invoice]);

	const onDateChange = useCallback(
		(datetime: DateTime) => {
			onInvoiceUpdated({
				...JSON.parse(JSON.stringify(invoiceRef.current)),
				date: datetime,
			});
		},
		[onInvoiceUpdated],
	);

	const onDescriptionChange = useCallback(
		(event: SyntheticEvent<HTMLTextAreaElement>) => {
			onInvoiceUpdated({
				...JSON.parse(JSON.stringify(invoiceRef.current)),
				date: invoiceRef.current.date,
				description: event.currentTarget.value,
			});
		},
		[onInvoiceUpdated],
	);

	const onCustomerChanged = useCallback(
		(customerId: string) => {
			onInvoiceUpdated({
				...JSON.parse(JSON.stringify(invoiceRef.current)),
				date: invoiceRef.current.date,
				customer: customerId,
			});
		},
		[onInvoiceUpdated],
	);

	const onLineItemUpdated = useCallback(
		(updatedLineItem: InvoiceLineItem) => {
			onInvoiceUpdated({
				...JSON.parse(JSON.stringify(invoiceRef.current)),
				date: invoiceRef.current.date,
				items: invoiceRef.current.items.map((item) => {
					return item.id === updatedLineItem.id ? updatedLineItem : item;
				}),
			});
		},
		[onInvoiceUpdated, invoiceRef],
	);

	return (
		<Box>
			<Box borderBottom='500' borderColor='container.border.base' pb='space.400' mb='space.400'>
				<Box width={300}>
					<FormField label='Customer'>
						{(props) => (
							<CustomerPicker
								{...props}
								selectedCustomerId={invoice.customer}
								onChange={onCustomerChanged}
							/>
						)}
					</FormField>
				</Box>
			</Box>
			<Box borderBottom='500' borderColor='container.border.base' mb='space.400'>
				{invoice.id && (
					<Box width={130}>
						<FormField label='Invoice ID'>
							{(props) => (
								<Input
									{...props}
									name='invoiceID'
									value={invoice.publicId?.toString().padStart(4, '0') ?? ''}
									disabled
								/>
							)}
						</FormField>
					</Box>
				)}

				<FormField label='Issue Date'>
					{(props) => <DatePicker {...props} value={invoice.date} onChange={onDateChange} />}
				</FormField>

				<FormField label='Description'>
					{(props) => (
						<Textarea
							{...props}
							name='description'
							value={invoice.description}
							onChange={onDescriptionChange}
						/>
					)}
				</FormField>
			</Box>
			<Box>
				<InvoiceLineItemsEditForm items={invoice.items} onChange={onLineItemUpdated} />
			</Box>
		</Box>
	);
};

export default InvoiceEditForm;
